import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  setComparisonDateRange,
  setIsFetchingLocationDetails,
  setSelectedStationIds,
} from '../../../../redux/slices/LocationComparisonSlice';
import type { RootState } from '../../../../redux/store';
import CustomDateRangePicker from '../../../../shared-components/custom-date-range/CustomDateRangePicker';
import ExportButton from '../../../../shared-components/export-button/ExportButton';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import ToFixedValue from '../../../../shared-functions/ToFixedValue';
import stationDetailsApi from '../../../../utils/api/location-management';

import LocationCard from './LocationCard';

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

export interface StationMetric {
  label: string;
  key: string;
}

export interface StationData {
  _id: string;
  name: string;
  location: string;
  totalRevenue: number;
  users: number;
  deviceCount: number;
  totalPowerConsumed?: number | string;
  energyDispensed: string;
  connectors: number;
  uptime: string;
  chargingSessions?: number;
  totalChargingSession?: number;
  totalChargingSessions?: number;
  sessionCount?: number;
  avgSessionDuration: string;
  healthScore: number;
  faultCount: number;
}

interface SelectOption {
  label: string;
  value: string;
}

/* -------------------------------------------------------------------------- */
/*                              Static Constants                              */
/* -------------------------------------------------------------------------- */

const MAX_COMPARE_COUNT = 4;

/* -------------------------------------------------------------------------- */
/*                              API Helper                                    */
/* -------------------------------------------------------------------------- */

/**
 * Fetch station details.
 * @param {string} id - Station ID.
 * @param {object} [range] - Optional date range parameters.
 * @param {string} [range.startDate] - Start date.
 * @param {string} [range.endDate] - End date.
 * @returns {Promise<StationData | null>} Station details or null.
 */
const fetchStation = async (
  id: string,
  range?: { startDate?: string; endDate?: string },
): Promise<StationData | null> => {
  try {
    const query: Record<string, any> = {};
    if (range?.startDate) {
      query.startDate = range.startDate;
    }
    if (range?.endDate) {
      query.endDate = range.endDate;
    }

    const response = await stationDetailsApi.fetchLocationDetails(
      id,
      Object.keys(query).length > 0 ? query : undefined,
    );
    return response.data as StationData;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Failed to fetch station ${id}`, error);
    return null;
  }
};

/**
 * LocationComparisonList component.
 * Manages comparison state, API fetching, date range filtering, export features,
 * and passes the unified data to the LocationCard grid.
 * @returns {React.ReactElement} The rendered component.
 */
function LocationComparisonList(): React.ReactElement {
  const { t, i18n } = useTranslation('sidePanel');
  const dispatch = useAppDispatch();

  const { chargingStationList, selectedChargingPoint } = useAppSelector(
    (state: RootState) => state.locationManagement,
  );
  const { dateRange: storedDateRange, selectedStationIds: storedSelectedStationIds } =
    useAppSelector((state: RootState) => state.locationComparison);

  const [isFetching, setIsFetching] = useState<boolean>(false);

  // Seed from Redux (not hardcoded defaults) so remounting this component —
  // e.g. switching away from and back to the Station Comparison tab, which
  // fully unmounts/remounts it — doesn't reset the filters shown here out of
  // sync with what the charts (which read Redux directly) are still using.
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>(
    () => storedDateRange || { startDate: '', endDate: '' },
  );

  const [selectedDevices, setSelectedDevices] = useState<string[]>(() => {
    if (storedSelectedStationIds?.length) {
      const restoredDevices = Array(MAX_COMPARE_COUNT).fill('');
      storedSelectedStationIds.forEach((id, index) => {
        if (index < MAX_COMPARE_COUNT) {
          restoredDevices[index] = id;
        }
      });
      return restoredDevices;
    }
    const initialDevices = Array(MAX_COMPARE_COUNT).fill('');
    if (selectedChargingPoint?.id) {
      initialDevices[0] = selectedChargingPoint.id;
    }
    return initialDevices;
  });

  const [detailCache, setDetailCache] = useState<Record<string, StationData>>({});

  const [chartToggle, setChartToggle] = useState([
    { label: t('fullDetails'), key: 'full', isActive: true },
    { label: t('differenceOnly'), key: 'diff', isActive: false },
  ]);

  const METRICS: StationMetric[] = useMemo(
    () => [
      { label: t('stationLocation'), key: 'address' },
      { label: t('deviceCount'), key: 'deviceCount' },
      { label: t('connectors'), key: 'totalConnectors' },
      { label: t('uptimeLast30Days'), key: 'uptimeLast30Days' },
      { label: t('sessionCount', t('noOfChargingSessions')), key: 'totalChargingSession' },
      { label: t('energyDispensedKwh'), key: 'totalPowerConsumed' },
      { label: t('totalRevenueThisMonth'), key: 'totalRevenue' },
      // { label: t('healthScore'), key: 'healthScore' },
      // { label: t('faultCountLast30Days'), key: 'faultsCount' },
    ],
    [t],
  );

  /* ------------------------------------------------------------------------ */
  /*                              Derived State                               */
  /* ------------------------------------------------------------------------ */

  const detailedStations = useMemo(
    () =>
      selectedDevices
        .map((id) => detailCache[id])
        .filter((station): station is StationData => Boolean(station)),
    [detailCache, selectedDevices],
  );

  const orderedDeviceOptions: SelectOption[] = useMemo(() => {
    if (!chargingStationList.length) {
      return [];
    }

    const selected = selectedDevices
      .filter(Boolean)
      .map((id) => chargingStationList.find((s) => s._id === id))
      .filter((station): station is { _id: string; name: string } => Boolean(station));

    const remaining = chargingStationList.filter(
      (station) => !selectedDevices.includes(station._id),
    );

    return [...selected, ...remaining].map((station) => ({
      label: station.name ? t(station.name) : station.name,
      value: station._id,
    }));
  }, [chargingStationList, selectedDevices, t]);

  /**
   * Number of visible selectors: shows all filled slots + the next empty slot to select from.
   */
  const visibleSelectorCount = useMemo(() => {
    const firstEmptyIndex = selectedDevices.findIndex((id) => !id);

    if (firstEmptyIndex === -1) {
      return MAX_COMPARE_COUNT;
    }

    return Math.max(1, firstEmptyIndex + 1);
  }, [selectedDevices]);

  /**
   * Handle export file generation.
   * @param {string} type - Export file type ('csv' or 'pdf').
   */
  const handleExport = async (type: string) => {
    const stationIds = selectedDevices.filter(Boolean);

    if (!stationIds.length) return;
    setIsFetching(true);

    try {
      const rangeParameter = dateRange.startDate && dateRange.endDate ? dateRange : undefined;
      const stations = (
        await Promise.all(stationIds.map((id) => fetchStation(id, rangeParameter)))
      ).filter(Boolean) as StationData[];

      const headers = [t('stationComparison'), ...stations.map((station) => station.name)];

      const rows = METRICS.map((metric) => [
        metric.label,
        ...stations.map((station) => {
          let value: any = '-';
          switch (metric.key) {
            case 'address':
              value = station.location || (station as any).address;
              break;
            case 'totalRevenue':
              value = station.totalRevenue;
              break;
            case 'consumerCount':
              value = station.users || (station as any).consumerCount;
              break;
            case 'deviceCount':
              value = station.deviceCount;
              break;
            case 'totalPowerConsumed':
            case 'electricityCostByUtilityProvider':
            case 'energyDispensed':
              value =
                station.totalPowerConsumed ??
                station.energyDispensed ??
                (station as any).electricityCostByUtilityProvider;
              break;
            case 'totalConnectors':
              value = station.connectors || (station as any).totalConnectors;
              break;
            case 'uptimeLast30Days':
              value = station.uptime || (station as any).uptimeLast30Days;
              break;
            case 'totalChargingSession':
            case 'chargingSessions':
            case 'totalChargingSessions':
            case 'sessionCount':
              value =
                station.totalChargingSession ??
                station.totalChargingSessions ??
                station.chargingSessions ??
                station.sessionCount;
              break;
            case 'avgChargingHours':
              value = station.avgSessionDuration || (station as any).avgChargingHours;
              break;
            // case 'healthScore':
            //   value = station.healthScore;
            //   break;
            // case 'faultsCount':
            //   value = station.faultCount || (station as any).faultsCount;
            //   break;
            default:
              value = (station as any)[metric.key];
          }

          if (value === undefined || value === null || value === '') {
            return '-';
          }

          if (typeof value === 'number') {
            if (!Number.isInteger(value)) {
              return String(ToFixedValue(value));
            }
            return String(value);
          }

          return String(value);
        }),
      ]);

      if (type === 'csv') {
        exportCSV(headers, rows, 'Station Comparison.csv');
      } else {
        exportPDF(headers, rows, 'Station Comparison.pdf');
      }
    } finally {
      setIsFetching(false);
    }
  };

  /* ------------------------------------------------------------------------ */
  /*                              Effects                                     */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (selectedChargingPoint?.id && !selectedDevices[0]) {
      setSelectedDevices((previous) => {
        const updated = [...previous];
        updated[0] = selectedChargingPoint.id;
        return updated;
      });
    }
  }, [selectedChargingPoint, selectedDevices]);

  useEffect(() => {
    dispatch(setSelectedStationIds(selectedDevices));

    const activeIds = selectedDevices.filter(Boolean);
    if (!activeIds.length) {
      return;
    }

    setIsFetching(true);
    dispatch(setIsFetchingLocationDetails(true));

    /**
     * Fetch all active stations and merge successful results into the cache.
     */
    const fetchAllStations = async () => {
      try {
        const rangeParameter = dateRange.startDate && dateRange.endDate ? dateRange : undefined;
        const results = await Promise.all(activeIds.map((id) => fetchStation(id, rangeParameter)));

        setDetailCache((previous) => {
          const updated = { ...previous };
          results.forEach((station) => {
            if (station) {
              updated[station._id] = station;
            }
          });
          return updated;
        });
      } finally {
        setIsFetching(false);
        dispatch(setIsFetchingLocationDetails(false));
      }
    };

    fetchAllStations();
  }, [dispatch, selectedDevices, dateRange.startDate, dateRange.endDate]);

  /* ------------------------------------------------------------------------ */
  /*                              Handlers                                    */
  /* ------------------------------------------------------------------------ */

  /**
   * Handle date range change.
   * @param {string} start - Start date.
   * @param {string} end - End date.
   */
  const handleDateRangeChange = useCallback(
    (start: string, end: string) => {
      const newRange = { startDate: start, endDate: end };
      setDateRange(newRange);
      dispatch(setComparisonDateRange(newRange));
    },
    [dispatch],
  );

  /**
   * Handle toggle click
   * @param {string} key - Toggle key.
   */
  const handleClickToggle = useCallback((key: string) => {
    setChartToggle((previous) =>
      previous.map((tab) => ({
        ...tab,
        isActive: tab.key === key,
      })),
    );
  }, []);

  /**
   * Handle dropdown value change
   * @param {number} index - Column index.
   * @param {string} value - Selected station ID.
   */
  const handleChangeFunction = useCallback((index: number, value: string) => {
    setSelectedDevices((previous) => {
      const updated = [...previous];
      updated[index] = value;
      return updated;
    });
  }, []);

  /**
   * Handle removing a station at a given index.
   * Shifts the following stations to the left and leaves the trailing slots empty.
   * @param {number} removeIndex - The index of the station to remove.
   */
  const handleRemoveStation = useCallback((removeIndex: number) => {
    setSelectedDevices((previous) => {
      const updated = previous.filter((_, index) => index !== removeIndex);
      while (updated.length < MAX_COMPARE_COUNT) {
        updated.push('');
      }
      return updated;
    });
  }, []);

  /**
   * Swap two comparison columns
   * @param {number} first - First index.
   * @param {number} second - Second index.
   */
  const swapColumns = useCallback((first: number, second: number) => {
    setSelectedDevices((previous) => {
      const updated = [...previous];
      [updated[first], updated[second]] = [updated[second], updated[first]];
      return updated;
    });
  }, []);

  /**
   * Filter dropdown options to avoid duplicate selection
   * @param {number} currentIndex - Current dropdown index.
   * @returns {SelectOption[]} Filtered options.
   */
  const getFilteredOptions = useCallback(
    (currentIndex: number): SelectOption[] =>
      orderedDeviceOptions.filter(
        (option) =>
          option.value === selectedDevices[currentIndex] || !selectedDevices.includes(option.value),
      ),
    [orderedDeviceOptions, selectedDevices],
  );

  /* ------------------------------------------------------------------------ */
  /*                              Render                                      */
  /* ------------------------------------------------------------------------ */

  return (
    <div className="location-comparison-list" dir={i18n.dir()}>
      <div className="time-period-toggle">
        <CustomDateRangePicker
          startDate={dateRange.startDate}
          endDate={dateRange.endDate}
          onChange={handleDateRangeChange}
          placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
          defaultPreset="last14Days"
        />
        <ExportButton
          label={t('exportData')}
          buttonList={[
            {
              key: 'csv',
              /**
               * Export CSV callback.
               */
              onClickFunction: () => handleExport('csv'),
              allow: true,
            },
            {
              key: 'pdf',
              /**
               * Export PDF callback.
               */
              onClickFunction: () => handleExport('pdf'),
              allow: true,
            },
          ]}
          loader={isFetching}
          placement="top"
        />
      </div>

      <LocationCard
        stations={detailedStations}
        metrics={METRICS}
        heading={t('stationComparison')}
        differenceOnly={chartToggle[1].isActive}
        selectedDevices={selectedDevices}
        visibleSelectorCount={visibleSelectorCount}
        detailCache={detailCache}
        getFilteredOptions={getFilteredOptions}
        onSelectStation={handleChangeFunction}
        onRemoveStation={handleRemoveStation}
        onSwapColumns={swapColumns}
        chartToggle={chartToggle}
        onClickToggle={handleClickToggle}
      />
    </div>
  );
}

export default LocationComparisonList;
